import { Box, Grid2 } from '@mui/material';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { CurationStatusBadge } from '@components/CurationStatusBadge';
import DataTable from '@components/DataTable';
import DeviceDataAttribution from '@components/DeviceDataAttribution';
import DrilldownReadingSummary from '@components/DrilldownReadingSummary';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import MedicalChart from '@components/MedicalChart';
import { RecordSourcePill } from '@components/RecordSourcePill';
import SkeletonLoader from '@components/SkeletonLoader';

import { DATATABLE_COLUMNS } from '@constants/columns';
import { VITAL_SIGN_TYPES } from '@constants/fhir';

import { useConversion } from '@hooks/useConversion';
import { useIsMobile } from '@hooks/useIsMobile';

import type { DataPointMapped } from '@models/data-point';

import { convertUnit, getUnit } from '@utils/conversion';
import { hasDeviceSourcedData } from '@utils/device-brand';
import { formatDate, formatVitalSign } from '@utils/format';

import type { CurationStatus, ResourceSourceType } from 'generated/types';

import { ContentWrapper, HardLeftDiv } from '../Drilldown/Drilldown.style';

const messages = defineMessages({
  past: {
    id: 'healthTrackerDrilldown.past',
    defaultMessage: 'Past {type}',
  },
  never: {
    id: 'healthTrackerDrilldown.never',
    defaultMessage: 'Never',
  },
  lastReading: {
    id: 'healthTrackerDrilldown.lastReading',
    defaultMessage: 'Last Reading:',
  },
});

interface MetricListProps {
  onRowClick?: (record: Record<string, any>) => void;
  showColumns?: {
    isVitall: boolean;
    isPractitioner: boolean;
  };
  data: DataPointMapped[];
  loading: boolean;
  code: string;
}

const MetricList: React.FC<MetricListProps> = ({
  onRowClick,
  showColumns,
  data,
  loading,
  code,
}) => {
  const { formatMessage } = useIntl();
  const mobile = useIsMobile();
  const {
    state: { conversion },
  } = useConversion();

  const VITAL_SIGN_TYPE_DATA =
    code in VITAL_SIGN_TYPES ? VITAL_SIGN_TYPES[code] : undefined;

  const keys = (VITAL_SIGN_TYPE_DATA?.keys ?? [])
    .map((o) => Object.values(o))
    .flat()
    .reduce<string[]>((acc, curr) => {
      if (!acc.includes(curr)) {
        acc.push(curr);
      }
      return acc;
    }, []);
  const format = VITAL_SIGN_TYPE_DATA?.format;
  const multiplier = VITAL_SIGN_TYPE_DATA?.multiplier;
  const precision = VITAL_SIGN_TYPE_DATA?.precision ?? 1;

  function dataToTable(data: DataPointMapped[]) {
    const dedupedData = data.reduce<Record<string, Record<string, any>>>(
      (acc, point) => {
        const { id } = point.record;
        if (id) {
          if (id in acc) {
            acc[id][point.type] = point.value;
            acc[id].common = { ...point, time: point.date };
          } else {
            acc[id] = {
              [point.type]: point.value,
              common: { ...point, time: point.date },
            };
          }
        }
        return acc;
      },
      {}
    );

    return Object.values(dedupedData);
  }

  const tableData = dataToTable(data);

  const newestRecords = (VITAL_SIGN_TYPE_DATA?.keys ?? []).flatMap(
    ({ common }) => {
      const match = data.find((d) => d.type === common && d.date);
      if (match) {
        const result = [
          {
            ...match,
            value: Number(
              convertUnit(
                match.value,
                match.unit,
                conversion[code] ? 'CON' : 'SI',
                precision,
                code
              )
            ),
            unit: getUnit(match.unit, conversion[code] ? 'CON' : 'SI', code),
          },
        ];
        return result;
      }
      return [];
    }
  );

  const columns = (
    code in DATATABLE_COLUMNS
      ? DATATABLE_COLUMNS[code]
      : DATATABLE_COLUMNS.default
  ).concat([
    {
      name: 'curationStatus',
      title: 'Curation Status',
      render: (status: CurationStatus) => (
        <CurationStatusBadge status={status} />
      ),
      show: Boolean(showColumns?.isVitall),
    },
    {
      name: 'recordSource',
      title: 'Record Source',
      render: (type: ResourceSourceType) => <RecordSourcePill type={type} />,
      show: Boolean(showColumns?.isVitall || showColumns?.isPractitioner),
    },
  ]);

  const newestRecord = newestRecords.at(0);

  return (
    <>
      {/* Sits directly under the shared record title. No devices link: the
      reader is not the patient and has no device list of their own. */}
      {hasDeviceSourcedData(data) ? (
        <Box sx={{ pb: 1, pt: 1 }}>
          <DeviceDataAttribution color="grey.300" showDevicesLink={false} />
        </Box>
      ) : null}

      <Grid2 container alignItems="center" justifyContent="center">
        <Grid2>
          <ContentWrapper>
            <SkeletonLoader loading={loading} variant="circular">
              <DrilldownReadingSummary
                source={newestRecord?.source}
                topText={`${formatMessage(messages.lastReading)} ${
                  newestRecord?.date
                    ? formatDate({
                        date: newestRecord.date,
                        formatter: VITAL_SIGN_TYPE_DATA?.graph?.showTime
                          ? 'datetime'
                          : 'date',
                      })
                    : formatMessage(messages.never)
                }`}
                units={newestRecord?.unit ?? ''}
                value={newestRecords.reduce<string>((acc, curr, index) => {
                  const formatted = formatVitalSign(
                    {
                      value: curr.value,
                      unit: curr.unit,
                    },
                    undefined,
                    format ? (value) => format(value, curr.unit) : undefined,
                    multiplier
                  );
                  acc =
                    acc +
                    formatted.trim() +
                    (index < newestRecords.length - 1 ? '/' : '');
                  return acc;
                }, '')}
              />
            </SkeletonLoader>
            <SkeletonLoader loading={loading}>
              {mobile ? (
                <HardLeftDiv>
                  <MedicalChart data={data} code={code} width="100%" />
                </HardLeftDiv>
              ) : null}
            </SkeletonLoader>
          </ContentWrapper>
        </Grid2>
        <Grid2>
          <SkeletonLoader loading={loading}>
            {!mobile && (
              <>
                <MedicalChart data={data} code={code} width="100%" />
              </>
            )}
          </SkeletonLoader>
        </Grid2>
      </Grid2>

      <ContentLoader lineCount={5} loading={loading}>
        <DataTable
          columns={columns}
          conversionGroup={code}
          conversionType={code}
          data={tableData.map((record) => {
            return {
              ...record.common,
              value: `${formatVitalSign(
                keys.map((key) => record[key] ?? '').filter((r) => Boolean(r)),
                undefined,
                format
                  ? (value) => format(value, record.common.unit)
                  : undefined
              )} ${record.common.unit ?? ''}`,
              date: formatDate({
                date: record.common.date,
                formatter: VITAL_SIGN_TYPE_DATA?.graph?.showTime
                  ? 'datetime'
                  : 'date',
              }),
            };
          })}
          onClick={(record: Record<string, any>) => onRowClick?.(record)}
          showConversion
          showFilter
          showHead
          sortBy="date"
          title={
            mobile
              ? formatMessage(messages.past, {
                  type: '',
                })
              : ''
          }
          //   title={
          //     mobile
          //       ? formatMessage(messages.past, {
          //           type: HEALTH_TRACKER_MAP[category].friendly,
          //         })
          //       : ''
          //   }
        />
      </ContentLoader>
    </>
  );
};

export default withErrorBoundary(MetricList, {
  FallbackComponent: ErrorBoundaryFallback,
});
